/**
 * Ideas — "Anyone can suggest an improvement. The Ops associate director decides, and we track what actually
 * ships." ADR-001 §11.11 (last row), guide §4 "Idea", prototype `VIEWS.ideas` / `ideaAct` / `askProof('idea')`.
 *
 * Lifecycle: new → approved | needs_info | declined; needs_info → new (the author answers);
 * approved → building → live (with proof) → result checked by a manager. Live needs proof, and the person who
 * marked it live never checks their own result (operator decision 12, applied here as for tasks).
 *
 * Who: `ideas.submit` raises (every role with it, today founder, ops AD, ops member, sales, marketing);
 * `ideas.decide` decides (today founder and ops AD). Visibility: deciders see every idea; everyone else sees their
 * own and every idea that was approved, is being built or is live. Anything else is 404.
 */
import { z } from 'zod';
import { IsoDate, PageQuery, Percent, Timestamp, Uuid, Version, page } from './common.js';
import { ClientName } from './requests.js';
import { UserRef } from './users.js';
/** `needs_info` is the prototype's `info` ("More detail asked"). */
export const IdeaStatus = z
    .enum(['new', 'needs_info', 'approved', 'building', 'live', 'declined'])
    .meta({ id: 'IdeaStatus' });
/** Prototype ISTAT labels. */
export const IDEA_STATUS_LABEL = {
    new: 'Waiting for a decision',
    needs_info: 'More detail asked',
    approved: 'Approved',
    building: 'Being built',
    live: 'Live',
    declined: 'Declined',
};
/** Prototype IDEA_STAGES — the filter pills. `wait` = new + needs_info. */
export const IdeaStage = z.enum(['all', 'wait', 'approved', 'building', 'live', 'declined']).meta({ id: 'IdeaStage' });
export const IdeaEffort = z.enum(['S', 'M', 'L']).meta({ id: 'IdeaEffort' });
/** Prototype effort choices. */
export const IDEA_EFFORT_LABEL = {
    S: 'Small, a few days',
    M: 'Medium, a few weeks',
    L: 'Large, a quarter',
};
export const IdeaLogEntry = z
    .object({ at: Timestamp, by: UserRef.nullable(), text: z.string() })
    .meta({ id: 'IdeaLogEntry' });
/** "Proof it is live" — the same proof a task closes with: a note or a link, optionally a file. */
export const IdeaProof = z
    .object({
    proof_kind: z.enum(['note', 'link']),
    proof_text: z.string().nullable(),
    proof_url: z.string().nullable(),
    proof_file_key: z.string().nullable(),
    no_attachment_needed: z.boolean(),
    by: UserRef,
    at: Timestamp,
    /** "Result looks right" — a manager who did not mark it live. */
    checked: z.boolean(),
    checked_by: UserRef.nullable(),
    checked_at: Timestamp.nullable(),
})
    .meta({ id: 'IdeaProof' });
export const Idea = z
    .object({
    id: Uuid,
    /** "The idea in one line". */
    title: z.string(),
    /** "What would we do?" — answers to "Ask for detail" are appended here, as in the prototype. */
    what: z.string().nullable(),
    /** "What do we gain?" */
    benefit: z.string(),
    /**
     * `null` = "All clients". The client's name only: sales and marketing read ideas too, and requesters see a
     * client's name, never its segment, priority or SPOC (Step 3 rule, requests.ts ClientName).
     */
    client: ClientName.nullable(),
    by: UserRef,
    created_at: Timestamp,
    effort: IdeaEffort,
    status: IdeaStatus,
    /** The decider's last note: the approval note, the question asked, or why it was declined. */
    note: z.string().nullable(),
    /** Who builds it (set on approval). */
    owner: UserRef.nullable(),
    target_on: IsoDate.nullable(),
    /** "What was done and what changed" — set when marked live. */
    result: z.string().nullable(),
    proof: IdeaProof.nullable(),
    /** The trail, oldest first. */
    log: z.array(IdeaLogEntry),
    version: Version,
    updated_at: Timestamp,
})
    .meta({ id: 'Idea' });
export const IdeaIdParams = z.object({ id: Uuid });
/** The four tiles: Suggested, Waiting for a decision, Live, Approval rate (counts over all ideas — no content). */
export const IdeaStats = z
    .object({
    suggested: z.number().int().nonnegative(),
    waiting: z.number().int().nonnegative(),
    live: z.number().int().nonnegative(),
    /** Of decided ideas, the share not declined; `null` when none is decided ("-"). */
    approval_rate_pct: Percent.nullable(),
})
    .meta({ id: 'IdeaStats' });
export const IdeaPage = page(Idea)
    .extend({
    stats: IdeaStats,
    /** Pill counts over the caller's visible list. */
    stage_counts: z.record(IdeaStage, z.number().int().nonnegative()),
})
    .meta({ id: 'IdeaPage' });
export const ListIdeasQuery = PageQuery.extend({
    /** Default: `wait` for deciders, `all` for everyone else (prototype `ID.f = 'auto'`). */
    stage: IdeaStage.optional(),
    client_id: Uuid.optional(),
    /** `me` = only ideas I suggested. */
    by: z.literal('me').optional(),
});
/** "Suggest an idea". Requesters do not see the effort field; it defaults to M. */
export const CreateIdeaBody = z
    .object({
    title: z.string().trim().min(3, 'Add the idea in one line').max(200),
    what: z.string().trim().max(2000).optional(),
    benefit: z.string().trim().min(3, 'Add what we gain').max(2000),
    /** Must be a client the caller can see (404 otherwise). Omit for "All clients". */
    client_id: Uuid.optional(),
    effort: IdeaEffort.default('M'),
})
    .strict();
/**
 * Approve (who builds it and a target date are required), ask for detail, or decline (a note is required, the
 * author sees it). Only from `new` or `needs_info` (409 otherwise).
 */
export const DecideIdeaBody = z
    .object({
    decision: z.enum(['approve', 'needs_info', 'decline']),
    owner_id: Uuid.optional(),
    target_on: IsoDate.optional(),
    note: z.string().trim().min(1).max(2000).optional(),
})
    .strict()
    .superRefine((b, ctx) => {
    if (b.decision === 'approve') {
        if (!b.owner_id)
            ctx.addIssue({ code: 'custom', path: ['owner_id'], message: 'Pick who builds it' });
        if (!b.target_on)
            ctx.addIssue({ code: 'custom', path: ['target_on'], message: 'Set a target date' });
    }
    else {
        if (!b.note)
            ctx.addIssue({ code: 'custom', path: ['note'], message: 'Add a note so they know why' });
        if (b.owner_id || b.target_on) {
            ctx.addIssue({ code: 'custom', path: ['owner_id'], message: 'Pick an owner and a date only when approving' });
        }
    }
});
/** "Add the detail asked for" — the author answers; the idea goes back to waiting for a decision. */
export const ReplyIdeaBody = z.object({ note: z.string().trim().min(1, 'Write an answer').max(2000) }).strict();
/**
 * Progress on the trail (ADR §11.11 `POST /ideas/{id}/log`):
 *  - `building` — "Start building" (decider; from `approved`).
 *  - `live` — "Mark live, with proof" (decider or the owner; from `building`). Proof and the result line required.
 *  - `result_checked` — "Result looks right" (decider; not the person who marked it live → 403).
 */
export const IdeaLogBody = z
    .discriminatedUnion('step', [
    z.object({ step: z.literal('building'), note: z.string().trim().min(1).max(2000).optional() }).strict(),
    z
        .object({
        step: z.literal('live'),
        /** "What was done and what changed". */
        result: z.string().trim().min(3, 'Say in a line what was done').max(2000),
        proof_kind: z.enum(['note', 'link']),
        proof_url: z.url({ protocol: /^https?$/, message: 'Paste the full link, starting with https://' }).max(2000).optional(),
        proof_file_key: z.string().min(1).max(500).optional(),
        no_attachment_needed: z.boolean().optional(),
    })
        .strict(),
    z.object({ step: z.literal('result_checked') }).strict(),
])
    .superRefine((b, ctx) => {
    if (b.step !== 'live')
        return;
    if (b.proof_kind === 'link' && !b.proof_url) {
        ctx.addIssue({ code: 'custom', path: ['proof_url'], message: 'Paste the link that shows it is live' });
    }
    if (!b.proof_url && !b.proof_file_key && !b.no_attachment_needed) {
        ctx.addIssue({
            code: 'custom',
            path: ['proof_file_key'],
            message: 'Attach a file, paste a link, or tick No attachment needed',
        });
    }
});
//# sourceMappingURL=ideas.js.map